/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Badge,
    Button,
    Col,
    FormGroup,
    FormText,
    Input,
    Label,
    Row,
} from "reactstrap";
import { callAvosApi } from "../../../../utils/useAvosApiHook";
import { toast } from "../../../../utils/toast";

interface Device {
    UID: string;
    name: string;
    type: string;
}

interface RootState {
    devices: {
        current: Device | null;
    };
}

interface DeviceState {
    online: boolean | null;
    temperature: number | null;
    uptime: number | null;
    led_state: string | null;
    auto_trigger_sec: number | null;
    trigger_count: number | null;
    tap_wait_ms: number | null;
    self_test_status: string | null;
    self_test_result: string | null;
    diagnostics: string | null;
}

interface SelfTestResult {
    type: string;
    status: "PASS" | "FAIL";
    measurements: {
        side_a: number;
        side_b: number;
        both_sides: number;
        average: number;
    };
    diagnostics: {
        balance_diff_percent: number;
        balance_ok: boolean;
        power_ratio: number;
        ratio_ok: boolean;
    };
    fault: string;
}

interface DiagnosticsData {
    wifi_signal_db: number;
    wifi_signal_percent: number;
    uptime: number;
    mcu_temperature: number;
    voltage_24v: number;
    voltage_12v: number;
    voltage_usb: number;
    modbus_address: number;
    ip_address: string;
    ssid: string;
    bssid: string;
    mac_address: string;
    firmware_version: string;
    side_balance_ok: boolean;
    ratio_ok: boolean;
    all_tests_pass: boolean;
    fault_description: string;
    accel_power_flag: boolean;
}

interface MeasurementData {
    firmness: number;
    event_id?: string | number;
    tap_number: number;
    total_taps: number;
    timestamp: string;
    firmness_data?: Array<{
        top_value: number;
        peak_width: number;
        sensor_id: number;
    }>;
}

interface LatestMeasurement {
    id: string;
    created: string;
    data: MeasurementData[];
}

/**
 * RipeTap Device Controls Component
 * Provides MQTT topic-based controls for RipeTap devices with real-time polling updates
 */
export function RipetapControls() {
    const device = useSelector((state: RootState) => state.devices.current);
    const [isLoadingState, setIsLoadingState] = useState<boolean>(false);
    const [deviceState, setDeviceState] = useState<DeviceState>({
        online: null,
        temperature: null,
        uptime: null,
        led_state: null,
        auto_trigger_sec: null,
        trigger_count: null,
        tap_wait_ms: null,
        self_test_status: null,
        self_test_result: null,
        diagnostics: null,
    });

    // Form state for write operations
    const [triggerEventId, setTriggerEventId] = useState<string>("");
    const [autoTriggerSec, setAutoTriggerSec] = useState<string>("");
    const [triggerCount, setTriggerCount] = useState<string>("");
    const [tapWaitMs, setTapWaitMs] = useState<string>("");
    const [printMessage, setPrintMessage] = useState<string>("");
    const [latestMeasurement, setLatestMeasurement] = useState<LatestMeasurement | null>(null);
    const [isLoadingMeasurement, setIsLoadingMeasurement] = useState<boolean>(false);
    const [selfTestStatus, setSelfTestStatus] = useState<string | null>(null);
    const [selfTestResult, setSelfTestResult] = useState<SelfTestResult | null>(null);
    const [isRunningTest, setIsRunningTest] = useState<boolean>(false);
    const [diagnosticsData, setDiagnosticsData] = useState<DiagnosticsData | null>(null);

    const fetchDeviceState = useCallback(async () => {
        if (!device?.UID) return;

        setIsLoadingState(true);
        try {
            const response = await callAvosApi(`/device/${device.UID}/state/stream`);
            setDeviceState(response.data as unknown as DeviceState);
        } catch (error) {
            toast.warning("Failed to fetch device state");
        } finally {
            setIsLoadingState(false);
        }
    }, [device?.UID]);

    const fetchLatestMeasurement = useCallback(async () => {
        if (!device?.UID) return;

        setIsLoadingMeasurement(true);
        try {
            const response = await callAvosApi(`/device/${device.UID}/measurements/latest`);
            setLatestMeasurement(response.data);
        } catch (error) {
            // Silently fail - measurement might not exist yet
        } finally {
            setIsLoadingMeasurement(false);
        }
    }, [device?.UID]);

    // Poll device state updates every 2 seconds
    useEffect(() => {
        if (!device || device.type !== "ripetap") {
            return undefined;
        }

        let isMounted = true;
        let pollInterval: NodeJS.Timeout | null = null;

        const startPolling = async () => {
            // Fetch initial state
            await fetchDeviceState();

            // Start polling every 2 seconds
            pollInterval = setInterval(async () => {
                if (!isMounted) return;

                try {
                    const response = await callAvosApi(`/device/${device.UID}/state/stream`);
                    const state = response.data as unknown as DeviceState;
                    setDeviceState(state);

                    // Extract self-test status from state
                    if (state.self_test_status) {
                        setSelfTestStatus(state.self_test_status);
                        if (state.self_test_status === "running") {
                            setIsRunningTest(true);
                        } else if (state.self_test_status === "idle") {
                            setIsRunningTest(false);
                        }
                    }

                    // Extract self-test result from state
                    if (state.self_test_result) {
                        try {
                            const result = JSON.parse(state.self_test_result);
                            setSelfTestResult(result);
                            // Result received, test is complete
                            setIsRunningTest(false);
                            setSelfTestStatus("idle");
                        } catch (e) {
                            // Invalid JSON, ignore silently
                        }
                    }

                    // Extract diagnostics from state
                    if (state.diagnostics) {
                        try {
                            const diagnostics = JSON.parse(state.diagnostics);
                            setDiagnosticsData(diagnostics);
                        } catch (e) {
                            // ignore parse errors
                        }
                    }
                } catch (error) {
                    // Silently fail - don't show error for polling failures
                }
            }, 2000);
        };

        startPolling();

        // Fetch initial measurement
        fetchLatestMeasurement();

        // Cleanup on unmount
        return () => {
            isMounted = false;
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [device?.UID, fetchDeviceState, fetchLatestMeasurement]);

    const publishMqtt = useCallback(async (topic: string, payload = "") => {
        if (!device?.UID) return;

        try {
            await callAvosApi(`/device/${device.UID}/mqtt/publish`, {
                method: "POST",
                data: { topic, payload: String(payload) },
            });
            toast.success(`Published to ${topic} ${payload ?? ""}`);
        } catch (error: any) {
            const errorMsg = error.response?.data?.detail || error.message;
            toast.error(`Error: ${errorMsg}`);
        }
    }, [device?.UID]);

    const handleTrigger = useCallback(async () => {
        await publishMqtt(`ripetap/${device?.UID}/trigger`, triggerEventId);
        // Refetch measurement after a short delay to allow processing
        setTimeout(() => fetchLatestMeasurement(), 2000);
    }, [publishMqtt, device?.UID, triggerEventId, fetchLatestMeasurement]);

    const handleLed = useCallback((value: string) => {
        publishMqtt(`ripetap/${device?.UID}/led`, value);
    }, [publishMqtt, device?.UID]);

    const handlePing = useCallback(() => {
        publishMqtt(`ripetap/${device?.UID}/ping`, "0");
    }, [publishMqtt, device?.UID]);

    const handleFindMyDevice = useCallback(() => {
        publishMqtt(`ripetap/${device?.UID}/findmydevice`, "1");
    }, [publishMqtt, device?.UID]);

    const handleSelfTest = useCallback(async () => {
        setIsRunningTest(true);
        setSelfTestStatus("running");
        setSelfTestResult(null);
        await publishMqtt(`ripetap/${device?.UID}/self_test`, "1");

        // Auto-reset status after 30 seconds if no result received
        const timeoutId = setTimeout(() => {
            setIsRunningTest(false);
            setSelfTestStatus("idle");
            toast.warning("Self-test timeout - no response from device");
        }, 30000);

        // Store timeout ID so it can be cleared if result comes back early
        return () => clearTimeout(timeoutId);
    }, [publishMqtt, device?.UID]);
    const handlePrint = useCallback(() => {
        if (printMessage) {
            publishMqtt(`ripetap/${device?.UID}/print`, printMessage);
            setPrintMessage("");
        }
    }, [publishMqtt, device?.UID, printMessage]);

    const handleDisconnectTest = useCallback((type: string) => {
        publishMqtt(`ripetap/${device?.UID}/disconnect_test`, type);
    }, [publishMqtt, device?.UID]);

    const handleAutoTrigger = useCallback(() => {
        publishMqtt(`ripetap/${device?.UID}/auto_trigger_sec`, autoTriggerSec || "");
    }, [publishMqtt, device?.UID, autoTriggerSec]);

    const handleTriggerCount = useCallback(() => {
        publishMqtt(`ripetap/${device?.UID}/trigger_count`, triggerCount || "");
    }, [publishMqtt, device?.UID, triggerCount]);

    const handleTapWait = useCallback(() => {
        publishMqtt(`ripetap/${device?.UID}/tap_wait_ms`, tapWaitMs || "");
    }, [publishMqtt, device?.UID, tapWaitMs]);

    const handleRefreshState = useCallback(() => {
        fetchDeviceState();
    }, [fetchDeviceState]);

    const getOnlineStatus = (): string => {
        if (deviceState.online === null) return "Unknown";
        if (deviceState.online === true) return "Yes";
        if (deviceState.online === false) return "Restarted";
        return "Unknown";
    };

    const getConnectionBadge = (): JSX.Element => {
        if (isLoadingState) {
            return <Badge color="info" className="ms-2">Loading...</Badge>;
        }
        if (deviceState.online === true) {
            return <Badge color="success" className="ms-2">Online</Badge>;
        }
        if (deviceState.online === false) {
            return <Badge color="warning" className="ms-2">Restarted</Badge>;
        }
        return <Badge color="secondary" className="ms-2">Unknown</Badge>;
    };

    if (!device || device.type !== "ripetap") {
        return <Alert color="warning">This device is not a RipeTap device.</Alert>;
    }

    return (
        <div className="pt-3">
            {/* Trigger Controls */}
            <section className="mb-4">
                <h3>Trigger Measurement</h3>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Event ID (optional)</Label>
                            <Input
                                type="text"
                                placeholder="Leave empty for no event_id"
                                value={triggerEventId}
                                onChange={(e) => setTriggerEventId(e.target.value)}
                            />
                            <FormText>
                                Provide an event_id to track this measurement, or leave empty for standalone measurement
                            </FormText>
                        </FormGroup>
                        <Button color="primary" onClick={handleTrigger}>
                            Trigger Measurement
                        </Button>
                    </Col>
                </Row>
            </section>

            {/* Self-Test */}
            <section className="mb-4">
                <h3>Self-Test</h3>
                <div css={css`
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 0.25rem;
                `}>
                    <Row className="mb-3">
                        <Col md={12}>
                            <Button
                                color="primary"
                                onClick={handleSelfTest}
                                disabled={isRunningTest}
                            >
                                {isRunningTest ? "🔄 Test Running..." : "▶️ Run Self-Test"}
                            </Button>
                            {selfTestStatus && (
                                <Badge
                                    color={selfTestStatus === "running" ? "warning" : "secondary"}
                                    className="ms-3"
                                >
                                    Status: {selfTestStatus}
                                </Badge>
                            )}
                        </Col>
                    </Row>

                    {selfTestResult && (
                        <>
                            <Row className="mb-2">
                                <Col md={12}>
                                    <h5>
                                        Test Result:
                                        <Badge
                                            color={selfTestResult.status === "PASS" ? "success" : "danger"}
                                            className="ms-2"
                                        >
                                            {selfTestResult.status}
                                        </Badge>
                                    </h5>
                                </Col>
                            </Row>

                            <Row className="mb-2">
                                <Col md={12}>
                                    <strong>Measurements:</strong>
                                </Col>
                            </Row>
                            <Row className="mb-2">
                                <Col md={3}>
                                    Side A: <strong>{selfTestResult.measurements.side_a.toFixed(1)}</strong>
                                </Col>
                                <Col md={3}>
                                    Side B: <strong>{selfTestResult.measurements.side_b.toFixed(1)}</strong>
                                </Col>
                                <Col md={3}>
                                    Both Sides: <strong>{selfTestResult.measurements.both_sides.toFixed(1)}</strong>
                                </Col>
                                <Col md={3}>
                                    Average: <strong>{selfTestResult.measurements.average.toFixed(1)}</strong>
                                </Col>
                            </Row>

                            <Row className="mb-2">
                                <Col md={12}>
                                    <strong>Diagnostics:</strong>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={3}>
                                    Balance Diff: <strong>{selfTestResult.diagnostics.balance_diff_percent.toFixed(1)}%</strong>
                                    {" "}
                                    <Badge color={selfTestResult.diagnostics.balance_ok ? "success" : "danger"}>
                                        {selfTestResult.diagnostics.balance_ok ? "✓" : "✗"}
                                    </Badge>
                                </Col>
                                <Col md={3}>
                                    Power Ratio: <strong>{selfTestResult.diagnostics.power_ratio.toFixed(2)}</strong>
                                    {" "}
                                    <Badge color={selfTestResult.diagnostics.ratio_ok ? "success" : "danger"}>
                                        {selfTestResult.diagnostics.ratio_ok ? "✓" : "✗"}
                                    </Badge>
                                </Col>
                                <Col md={6}>
                                    {selfTestResult.fault && (
                                        <span className="text-danger">
                                            <strong>Fault:</strong> {selfTestResult.fault}
                                        </span>
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}

                    {!selfTestResult && !isRunningTest && (
                        <Row>
                            <Col>
                                <small className="text-muted">
                                    Click &quot;Run Self-Test&quot; to test the device sensors and diagnostics
                                </small>
                            </Col>
                        </Row>
                    )}
                </div>
            </section>

            {/* Diagnostics */}
            {diagnosticsData && (
                <section className="mb-4">
                    <h3>Diagnostics</h3>
                    <div css={css`
                        background: #f8f9fa;
                        padding: 1rem;
                        border-radius: 0.25rem;
                    `}>
                        <Row className="mb-2">
                            <Col md={12}>
                                <h5>
                                    System Status:
                                    <Badge
                                        color={diagnosticsData.all_tests_pass ? "success" : "danger"}
                                        className="ms-2"
                                    >
                                        {diagnosticsData.all_tests_pass ? "✓ All Tests Pass" : "✗ Issues Detected"}
                                    </Badge>
                                </h5>
                            </Col>
                        </Row>

                        <Row className="mb-2">
                            <Col md={12}>
                                <strong>WiFi & Network:</strong>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                Signal: <strong>{diagnosticsData.wifi_signal_db} dB ({diagnosticsData.wifi_signal_percent}%)</strong>
                            </Col>
                            <Col md={3}>
                                IP: <strong>{diagnosticsData.ip_address}</strong>
                            </Col>
                            <Col md={3}>
                                SSID: <strong>{diagnosticsData.ssid}</strong>
                            </Col>
                            <Col md={3}>
                                MAC: <strong>{diagnosticsData.mac_address}</strong>
                            </Col>
                        </Row>

                        <Row className="mb-2">
                            <Col md={12}>
                                <strong>Hardware:</strong>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={3}>
                                Temperature: <strong>{diagnosticsData.mcu_temperature}°C</strong>
                            </Col>
                            <Col md={3}>
                                Uptime: <strong>{diagnosticsData.uptime}s</strong>
                            </Col>
                            <Col md={3}>
                                Firmware: <strong>{diagnosticsData.firmware_version}</strong>
                            </Col>
                            <Col md={3}>
                                Modbus Addr: <strong>{diagnosticsData.modbus_address}</strong>
                            </Col>
                        </Row>

                        <Row className="mb-2">
                            <Col md={12}>
                                <strong>Power:</strong>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={4}>
                                24V: <strong>{diagnosticsData.voltage_24v.toFixed(2)}V</strong>
                            </Col>
                            <Col md={4}>
                                12V: <strong>{diagnosticsData.voltage_12v.toFixed(2)}V</strong>
                            </Col>
                            <Col md={4}>
                                USB: <strong>{diagnosticsData.voltage_usb.toFixed(2)}V</strong>
                            </Col>
                        </Row>

                        <Row className="mb-2">
                            <Col md={12}>
                                <strong>Sensor Status:</strong>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                Side Balance:
                                {" "}
                                <Badge color={diagnosticsData.side_balance_ok ? "success" : "danger"}>
                                    {diagnosticsData.side_balance_ok ? "✓ OK" : "✗ Failed"}
                                </Badge>
                            </Col>
                            <Col md={4}>
                                Ratio:
                                {" "}
                                <Badge color={diagnosticsData.ratio_ok ? "success" : "danger"}>
                                    {diagnosticsData.ratio_ok ? "✓ OK" : "✗ Failed"}
                                </Badge>
                            </Col>
                            <Col md={4}>
                                Accel Power:
                                {" "}
                                <Badge color={diagnosticsData.accel_power_flag ? "success" : "secondary"}>
                                    {diagnosticsData.accel_power_flag ? "✓ On" : "Off"}
                                </Badge>
                            </Col>
                        </Row>

                        {diagnosticsData.fault_description && (
                            <Row className="mt-3">
                                <Col md={12}>
                                    <Alert color="danger" className="mb-0">
                                        <strong>Fault:</strong> {diagnosticsData.fault_description}
                                    </Alert>
                                </Col>
                            </Row>
                        )}
                    </div>
                </section>
            )}

            {/* Device Status */}
            <section className="mb-4">
                <h3>
                    Device Status
                    {getConnectionBadge()}
                </h3>
                <div css={css`
                    background: #f8f9fa;
                    padding: 1rem;
                    border-radius: 0.25rem;
                `}>
                    <Row>
                        <Col md={3}>
                            <strong>Online:</strong>{" "}
                            {getOnlineStatus()}
                        </Col>
                        <Col md={3}>
                            <strong>Temperature:</strong> {deviceState.temperature ? `${deviceState.temperature}°C` : "N/A"}
                        </Col>
                        <Col md={3}>
                            <strong>Uptime:</strong> {deviceState.uptime ? `${deviceState.uptime}s` : "N/A"}
                        </Col>
                        <Col md={3}>
                            <strong>LED:</strong> {deviceState.led_state || "Unknown"}
                        </Col>
                    </Row>
                </div>
                <div className="mt-2 d-flex gap-2">
                    <Button color="secondary" size="sm" onClick={handlePing}>
                        Ping Device
                    </Button>
                    <Button color="primary" size="sm" onClick={handleRefreshState}>
                        Refresh State
                    </Button>
                    <Button color="info" size="sm" onClick={handleFindMyDevice}>
                        🪩 Find My Device (Disco)
                    </Button>
                </div>
            </section>

            {/* Latest Measurement */}
            <section className="mb-4">
                <h3>Latest Measurement</h3>
                {isLoadingMeasurement && (
                    <div css={css`
                        background: #f8f9fa;
                        padding: 1rem;
                        border-radius: 0.25rem;
                        text-align: center;
                    `}>
                        <span>Loading...</span>
                    </div>
                )}
                {!isLoadingMeasurement && latestMeasurement && latestMeasurement.data.length > 0 && (
                    <div css={css`
                        background: #f8f9fa;
                        padding: 1rem;
                        border-radius: 0.25rem;
                    `}>
                        {latestMeasurement.data.map((tap, index) => (
                            <Row key={index} className={index > 0 ? "mt-2 pt-2 border-top" : ""}>
                                <Col md={3}>
                                    <strong>Tap {tap.tap_number}/{tap.total_taps}:</strong>
                                </Col>
                                <Col md={3}>
                                    <strong>Firmness:</strong> {tap.firmness.toFixed(2)}
                                </Col>
                                <Col md={3}>
                                    <strong>Timestamp:</strong> {new Date(tap.timestamp).toLocaleTimeString()}
                                </Col>
                                <Col md={3}>
                                    {tap.event_id && (
                                        <><strong>Event ID:</strong> {tap.event_id}</>
                                    )}
                                </Col>
                            </Row>
                        ))}
                        <Row className="mt-2">
                            <Col>
                                <small className="text-muted">
                                    Measurement ID: {latestMeasurement.id} | Stored: {new Date(latestMeasurement.created).toLocaleString()}
                                </small>
                            </Col>
                        </Row>
                    </div>
                )}
                {!isLoadingMeasurement && (!latestMeasurement || latestMeasurement.data.length === 0) && (
                    <div css={css`
                        background: #f8f9fa;
                        padding: 1rem;
                        border-radius: 0.25rem;
                        text-align: center;
                        color: #6c757d;
                    `}>
                        <span>No measurements available yet</span>
                    </div>
                )}
                <div className="mt-2">
                    <Button color="primary" size="sm" onClick={fetchLatestMeasurement}>
                        Refresh Measurement
                    </Button>
                </div>
            </section>

            {/* LED Control */}
            <section className="mb-4">
                <h3>LED Control</h3>
                <Row>
                    <Col md={6}>
                        <div className="d-flex gap-2">
                            <Button color="success" onClick={() => handleLed("On")}>
                                LED On
                            </Button>
                            <Button color="danger" onClick={() => handleLed("Off")}>
                                LED Off
                            </Button>
                        </div>
                        <FormText className="mt-2">
                            Current LED state: {deviceState.led_state || "Unknown"}
                        </FormText>
                    </Col>
                </Row>
            </section>

            {/* Measurement Configuration */}
            <section className="mb-4">
                <h3>Measurement Configuration</h3>
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label>Auto Trigger Interval (seconds)</Label>
                            <Input
                                type="number"
                                min="0"
                                placeholder="0 = disabled, >10 = interval"
                                value={autoTriggerSec}
                                onChange={(e) => setAutoTriggerSec(e.target.value)}
                            />
                            <FormText>
                                Current: {deviceState.auto_trigger_sec !== null ? deviceState.auto_trigger_sec : "N/A"}
                                <br />
                                0 = disabled, empty to query state, &gt;10 to set interval
                            </FormText>
                        </FormGroup>
                        <Button color="primary" onClick={handleAutoTrigger}>
                            Update Auto Trigger
                        </Button>
                    </Col>

                    <Col md={4}>
                        <FormGroup>
                            <Label>Trigger Count (taps)</Label>
                            <Input
                                type="number"
                                min="1"
                                max="10"
                                placeholder="1-10 taps"
                                value={triggerCount}
                                onChange={(e) => setTriggerCount(e.target.value)}
                            />
                            <FormText>
                                Current: {deviceState.trigger_count !== null ? deviceState.trigger_count : "N/A"}
                                <br />
                                Number of taps per measurement (1-10), empty to query state
                            </FormText>
                        </FormGroup>
                        <Button color="primary" onClick={handleTriggerCount}>
                            Update Trigger Count
                        </Button>
                    </Col>

                    <Col md={4}>
                        <FormGroup>
                            <Label>Tap Wait Time (milliseconds)</Label>
                            <Input
                                type="number"
                                min="0"
                                max="60000"
                                placeholder="0-60000 ms"
                                value={tapWaitMs}
                                onChange={(e) => setTapWaitMs(e.target.value)}
                            />
                            <FormText>
                                Current: {deviceState.tap_wait_ms !== null ? deviceState.tap_wait_ms : "N/A"}
                                <br />
                                Wait time between taps (max 60000ms), empty to query state
                            </FormText>
                        </FormGroup>
                        <Button color="primary" onClick={handleTapWait}>
                            Update Tap Wait
                        </Button>
                    </Col>
                </Row>
            </section>

            {/* Debug Tools */}
            <section className="mb-4">
                <h3>Debug Tools</h3>
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Print Debug Message</Label>
                            <Input
                                type="text"
                                placeholder="Debug message to MCU"
                                value={printMessage}
                                onChange={(e) => setPrintMessage(e.target.value)}
                            />
                        </FormGroup>
                        <Button color="secondary" onClick={handlePrint}>
                            Send to MCU
                        </Button>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>Disconnect Test</Label>
                            <div className="d-flex gap-2">
                                <Button color="warning" size="sm" onClick={() => handleDisconnectTest("mqtt")}>
                                    Test MQTT Disconnect
                                </Button>
                                <Button color="warning" size="sm" onClick={() => handleDisconnectTest("wifi")}>
                                    Test WiFi Disconnect
                                </Button>
                                <Button color="warning" size="sm" onClick={() => handleDisconnectTest("full")}>
                                    Test Full Disconnect
                                </Button>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </section>

            {/* Help Section */}
            <section className="mb-4">
                <Alert color="info">
                    <h5>RipeTap MQTT Topics</h5>
                    <ul className="mb-0">
                        <li><code>trigger</code> - Start measurement (with optional event_id)</li>
                        <li><code>online</code> - Device online status (0=restarted, 1=online, sent every minute)</li>
                        <li><code>temperature</code> - MCU temperature (&gt;50°C = too hot)</li>
                        <li><code>uptime</code> - Seconds since boot (response to ping)</li>
                        <li><code>led</code> - Control LED state (On/Off or 1/0)</li>
                        <li><code>measurement</code> - Measurement data (JSON)</li>
                        <li><code>auto_trigger_sec</code> - Auto-trigger interval (0=off, &gt;10=enabled)</li>
                        <li><code>trigger_count</code> - Number of taps per measurement (1-10)</li>
                        <li><code>tap_wait_ms</code> - Wait time between taps (max 60000ms)</li>
                        <li><code>findmydevice</code> - Trigger disco mode (device blinks to identify itself)</li>
                        <li><code>self_test</code> - Run device self-test diagnostics</li>
                        <li><code>self_test/status</code> - Self-test status (running/idle)</li>
                        <li><code>self_test/result</code> - Self-test results (JSON with measurements and diagnostics)</li>
                        <li><code>diagnostics</code> - Device diagnostics (JSON with WiFi, hardware, power, and sensor status)</li>
                    </ul>
                </Alert>
            </section>
        </div>
    );
}


